import React, { useState } from 'react';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Paper,
    Button,
    Typography,
    CircularProgress,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Box,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import { Stack } from '@mui/system';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import SouthIcon from '@mui/icons-material/South';
import NorthIcon from '@mui/icons-material/North';
import { darkBlue, darkBlue2, white } from '@styles/colors';
import { Company } from '@staffedupusa/shared-components';
import { useGetrockerboxEmployers } from '@features/rockerboxDashboard/hooks/useGetrockerboxEmployers';
import RockerboxListingRow from '@features/rockerboxDashboard/components/rockerboxListingRow';

function RockerboxDashboard() {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchValue, setSearchValue] = useState('');
    const [columnNo, setColumnNo] = useState(0);
    const [sortedDirection, setSortedDirection] = useState('asc');
    const [applicationsListAlertText, setApplicationsListAlertText] = useState<string>('');
    const [statusValue, setStatusValue] = useState<string | boolean>('all');

    const { rockerboxList, isLoadingrockerboxList } = useGetrockerboxEmployers({
        searchValue,
        page,
        columnNo,
        sortedDirection,
        page_length: rowsPerPage,
        enabled: statusValue,
        setAlertText: setApplicationsListAlertText,
    });

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage + 1);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    const handleSearch = () => {
        setPage(1);
    };

    const handleSort = (column: number) => {
        const isAsc = columnNo === column && sortedDirection === 'asc';
        setSortedDirection(isAsc ? 'desc' : 'asc');
        setColumnNo(column);
    };

    const renderSortArrow = (column: number) => {
        if (columnNo === column) {
            return sortedDirection === 'asc' ? (
                <Stack direction='row' >
                    <NorthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
                    <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                </Stack>
            ) : (
                <Stack direction='row'>
                    <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                    <SouthIcon fontSize='small' color="primary" sx={{ margin: "0 -1px", width: "1rem" }} />
                </Stack>
            );
        }
        return (
            <Stack direction='row'>
                <NorthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
                <SouthIcon fontSize='small' style={{ opacity: 0.5 }} sx={{ margin: "0 -1px", width: "1rem" }} />
            </Stack>
        );
    };

    return (
        <div>
            <Helmet>
                <title>Rockerbox | StaffedUp</title>
            </Helmet>
            <Stack component="section" spacing={5} pt={5} pb={4} px={3} pl={6}>
                <Stack p={3} borderRadius='8px' bgcolor={white} direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between">
                    <Typography variant="h3" fontSize="28px" fontWeight="bold">
                        Rockerbox Dashboard
                    </Typography>
                </Stack>
                <Stack spacing={4} sx={{ borderRadius: 5 }} direction={{ xs: 'column', sm: 'row' }} justifyContent={{ sm: 'space-between' }} alignItems="center">
                    <Paper component="form" sx={{ pl: '10px', display: 'flex', alignItems: 'center', maxWidth: 400, width: '100%', borderRadius: 5 }}>
                        <InputBase
                            sx={{ ml: 1, flex: 1 }}
                            placeholder="Search"
                            inputProps={{ 'aria-label': 'search' }}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                        <IconButton type="button" sx={{ px: '10px' }} aria-label="search" onClick={handleSearch}>
                            <SearchIcon />
                        </IconButton>
                    </Paper>
                    <ToggleButtonGroup
                        value={statusValue}
                        exclusive
                        onChange={(event, newValue) => {
                            setStatusValue(newValue);
                        }}
                    >
                        <ToggleButton
                            sx={{
                                backgroundColor: statusValue === 'all' ? darkBlue2 : 'default',
                                color: statusValue === 'all' ? 'white' : 'default',
                                '&.Mui-selected': {
                                    backgroundColor: darkBlue2,
                                    color: 'white',
                                },
                                padding: "5px 30px", borderBottomLeftRadius: '5px', borderTopLeftRadius: '5px'
                            }}
                            disabled={statusValue === 'all'}
                            value="all">All
                        </ToggleButton>
                        <ToggleButton
                            sx={{
                                backgroundColor: statusValue == 'true' ? darkBlue2 : 'default',
                                color: statusValue == 'true' ? 'white' : 'default',
                                '&.Mui-selected': {
                                    backgroundColor: darkBlue2,
                                    color: 'white',
                                },
                                padding: "5px 30px", borderBottomRightRadius: '5px', borderTopRightRadius: '5px'
                            }}
                            disabled={statusValue === true}
                            value={true}>
                            On
                        </ToggleButton>
                        <ToggleButton sx={{
                            backgroundColor: statusValue == 'false' ? darkBlue2 : 'default',
                            color: statusValue == 'false' ? 'white' : 'default',
                            '&.Mui-selected': {
                                backgroundColor: darkBlue2,
                                color: 'white',
                            },
                            padding: "5px 30px", borderBottomRightRadius: '5px', borderTopRightRadius: '5px'
                        }}
                            disabled={statusValue === false}

                            value={false}>
                            Off
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {/* {isLoadingrockerboxList ? (
                    <Box sx={{
                        display: 'flex', alignItems: "center", justifyContent: "center",
                        width: '100%', height: '300px'
                    }}>
                        <CircularProgress />
                    </Box>
                ) : rockerboxList?.object?.employers?.length > 0 ? (
                    <> */}
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell onClick={() => handleSort(0)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Status</b> {renderSortArrow(0)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(1)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Account Name</b> {renderSortArrow(1)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(2)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Contact Name</b> {renderSortArrow(2)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(3)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Contact Email</b> {renderSortArrow(3)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(4)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Location</b> {renderSortArrow(4)}
                                    </Stack>
                                </TableCell>
                                <TableCell onClick={() => handleSort(5)}>
                                    <Stack direction='row' justifyContent='space-between'>
                                        <b>Last Login</b> {renderSortArrow(5)}
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoadingrockerboxList ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Box sx={{
                                            display: 'flex', alignItems: "center", justifyContent: "center",
                                            textAlign: "center", alignSelf: "center",
                                            width: 'auto', height: '300px', margin: "auto"
                                        }}>

                                            <CircularProgress />
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : rockerboxList?.object?.employers?.length > 0 ? (
                                <>
                                    {rockerboxList.object.employers.map(
                                        (row: {
                                            email: String;
                                            name: String;
                                            num_logins: String;
                                            last_login: Date;
                                            id: number;
                                            company: Company;
                                        }) => (
                                            <RockerboxListingRow row={row} />
                                        ),
                                    )}
                                </>
                            ) : rockerboxList?.object?.employers?.length < 1 ? (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Box sx={{
                                            display: 'flex', alignItems: "center", justifyContent: "center",
                                            textAlign: "center", alignSelf: "center",
                                            width: 'auto', height: '300px', margin: "auto"
                                        }}>

                                            No Data Found
                                        </Box>
                                    </TableCell>
                                </TableRow>

                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6}>
                                        <Box sx={{
                                            display: 'flex', alignItems: "center", justifyContent: "center",
                                            textAlign: "center", alignSelf: "center",
                                            width: 'auto', height: '300px', margin: "auto"
                                        }}>

                                            {applicationsListAlertText}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rockerboxList?.object?.total_employers || 0}
                    rowsPerPage={rowsPerPage}
                    page={page - 1}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
                {/*   </>
             ) : rockerboxList?.object?.employers?.length < 1 ? (
                     <>No Data Found</>
                ) : (
                     <>{applicationsListAlertText}</>
                )} */}
            </Stack>
        </div >
    );
}

export default RockerboxDashboard;
